/** Reprice-vs-current-catalog diff (spec §2.5). Pure: session basis + candidate
 *  snapshot + the exec's overrides -> per-line price movement. Overrides are
 *  re-applied on the candidate ("the exec's word survives; the catalog moves").
 *  A SKU the candidate can no longer price (superseded away) surfaces as
 *  newPrice null — the UI shows it; nothing throws. */
import type { CatalogSnapshot } from './engine';
import type { PricingOverrides } from '../domain/PricingOverrides';
import { applyOverrides } from './applyOverrides';

export interface RepriceLine {
  productSku: string;
  oldPrice: number | null;
  newPrice: number | null;
  deltaDollars: number;
}
export interface RepriceDiff {
  lines: RepriceLine[];
  changedComponentCount: number;
  identical: boolean;
}

export function computeRepriceDiff(
  current: CatalogSnapshot, candidate: CatalogSnapshot, overrides: PricingOverrides,
): RepriceDiff {
  const oldPrices = applyOverrides(current, overrides).customPriceDollars;
  const newPrices = applyOverrides(candidate, overrides).customPriceDollars;
  const skus = [...new Set([...Object.keys(oldPrices), ...Object.keys(newPrices)])].sort();

  const lines: RepriceLine[] = skus.map((productSku) => {
    const oldPrice = oldPrices[productSku] ?? null;
    const newPrice = newPrices[productSku] ?? null;
    const deltaDollars = oldPrice !== null && newPrice !== null
      ? Math.round((newPrice - oldPrice) * 100) / 100 : 0;
    return { productSku, oldPrice, newPrice, deltaDollars };
  });

  let changedComponentCount = 0;
  const compSkus = new Set([
    ...Object.keys(current.componentCostMicros), ...Object.keys(candidate.componentCostMicros)]);
  for (const sku of compSkus) {
    if (current.componentCostMicros[sku] !== candidate.componentCostMicros[sku]) changedComponentCount += 1;
  }
  if (current.integrationBp !== candidate.integrationBp) changedComponentCount += 1;

  const identical = changedComponentCount === 0
    && lines.every(l => l.deltaDollars === 0 && l.newPrice !== null);
  return { lines, changedComponentCount, identical };
}
